import React, { useState, useEffect } from 'react';
import { useCart } from '../../contexts/CartContext';
import CheckoutAPI, { useCheckout } from '../../services/checkout_api';
import './Checkout.css';
import LoadingSpinner from '../Common/LoadingSpinner';

const Checkout = ({ setCurrentPage, setPageData, onBack }) => {
  const { cart, getCartSummary, formatPrice, clearCart } = useCart();
  const { placeOrder, loading: apiLoading, error: apiError } = useCheckout();
  
  const [formData, setFormData] = useState({
    // Customer Information
    fullName: '',
    email: '',
    phone: '',
    company: '',
    
    // Shipping Address
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Pakistan',
    
    // Delivery Preferences
    deliveryTimePreference: 'anytime',
    specialInstructions: '',
    orderNotes: ''
  });

  // ✅ NEW STATE - Installation Service
  const [requiresInstallation, setRequiresInstallation] = useState(false);
  const [installationAddress, setInstallationAddress] = useState('same'); // 'same' or 'different'
  const [installationAddressDetails, setInstallationAddressDetails] = useState('');

  const [formErrors, setFormErrors] = useState({});
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const summary = getCartSummary();

  useEffect(() => {
    if (summary.isEmpty) {
      setCurrentPage('cart');
    }
  }, [summary.isEmpty, setCurrentPage]);

  useEffect(() => {
    if (apiError || submitError) {
      setSubmitError(null);
    }
  }, [formData, requiresInstallation]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^[\+]?[\d\s\-\(\)]{10,}$/.test(formData.phone.replace(/\s/g, ''))) {
      errors.phone = 'Please enter a valid phone number';
    }

    if (!formData.address.trim()) {
      errors.address = 'Address is required';
    }

    if (!formData.city.trim()) {
      errors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      errors.state = 'State/Province is required';
    }

    if (!formData.zipCode.trim()) {
      errors.zipCode = 'Zip/Postal code is required';
    }

    // ✅ NEW - Validate installation address if different
    if (requiresInstallation && installationAddress === 'different' && !installationAddressDetails.trim()) {
      errors.installationAddress = 'Installation address is required';
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      const firstError = Object.keys(errors)[0];
      const element = document.getElementsByName(firstError)[0];
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSubmitError(null);

    try {
      // ✅ UPDATED - Include installation data
      const orderData = {
        customerInfo: {
          fullName: formData.fullName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          company: formData.company.trim() || null
        },
        shippingAddress: {
          address: formData.address.trim(),
          city: formData.city.trim(),
          state: formData.state.trim(),
          zipCode: formData.zipCode.trim(),
          country: formData.country
        },
        items: cart.items.map(item => ({
          id: item.id,
          type: item.type || 'product',
          title: item.title,
          sku: item.sku || `${item.type}-${item.id}`,
          price: parseFloat(item.price),
          quantity: parseInt(item.quantity),
          category: item.category || null,
          image: item.image || null,
          isOnSale: item.isOnSale || false,
          originalPrice: item.originalPrice || null
        })),
        deliveryTimePreference: formData.deliveryTimePreference,
        specialInstructions: formData.specialInstructions.trim() || null,
        orderNotes: formData.orderNotes.trim() || null,
        paymentMethod: 'cod',
        // ✅ NEW - Installation fields
        requiresInstallation: requiresInstallation,
        installationAddress: requiresInstallation 
          ? (installationAddress === 'same' 
              ? `${formData.address}, ${formData.city}, ${formData.state} ${formData.zipCode}` 
              : installationAddressDetails.trim())
          : null,
        totals: {
          subtotal: summary.totalAmount.toFixed(2),
          tax: '0.00',
          shipping: '0.00',
          discount: '0.00',
          total: summary.totalAmount.toFixed(2)
        }
      };

      console.log('Submitting order data:', orderData);

      const createdOrder = await placeOrder(orderData);
      
      if (createdOrder) {
        setOrderDetails(createdOrder);
        setOrderPlaced(true);
        clearCart();
        console.log('Order placed successfully:', createdOrder);
      }

    } catch (error) {
      console.error('Error placing order:', error);
      
      let errorMessage = 'There was an error placing your order. Please try again.';
      
      if (error.errors && Array.isArray(error.errors)) {
        errorMessage = error.errors.join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSubmitError(errorMessage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ✅ UPDATED - Order confirmation with tracking link
  if (orderPlaced && orderDetails) {
    return (
      <div className="checkout-page">
        <section className="order-confirmation">
          <div className="container">
            <div className="confirmation-content">
              <div className="success-icon" style={{ color: '#0d9488', display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
                <svg viewBox="0 0 24 24" width="60" height="60" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h1>Order Placed Successfully!</h1>
              <p className="order-number">
                Order Number: <strong>{orderDetails.order_number || orderDetails.orderNumber || orderDetails.id}</strong>
              </p>
              
              <div className="order-summary-box">
                <h3>Order Summary</h3>
                <p><strong>Total Amount:</strong> {formatPrice(orderDetails.total || orderDetails.totals?.total || summary.totalAmount)}</p>
                <p><strong>Payment Method:</strong> Cash on Delivery (COD)</p>
                <p><strong>Status:</strong> {orderDetails.status || 'Pending'}</p>
                {orderDetails.created_at && (
                  <p><strong>Order Date:</strong> {new Date(orderDetails.created_at).toLocaleDateString()}</p>
                )}
                {/* ✅ NEW - Show installation status */}
                {requiresInstallation && (
                  <p><strong>Installation:</strong> <span className="install-pending-badge">Pending - We'll contact you soon</span></p>
                )}
              </div>

              <div className="delivery-info">
                <h3>What's Next?</h3>
                <ul>
                  <li>We'll call you within 24 hours to confirm your order</li>
                  {requiresInstallation && <li>Our installation team will schedule a convenient time with you</li>}
                  <li>Your items will be prepared for delivery</li>
                  <li>Our delivery team will contact you before arrival</li>
                  <li>Pay cash when your order arrives</li>
                </ul>
              </div>

              <div className="confirmation-actions">
                {/* ✅ NEW - Track Order Button */}
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    setPageData({ orderNumber: orderDetails.order_number || orderDetails.orderNumber });
                    setCurrentPage('order-tracking');
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                    <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    Track Your Order
                  </span>
                </button>
                
                <button 
                  className="btn btn-secondary"
                  onClick={() => setCurrentPage('home')}
                >
                  Continue Shopping
                </button>
                
                <button 
                  className="btn btn-secondary"
                  onClick={() => window.print()}
                >
                  Print Receipt
                </button>
                
                {(orderDetails.order_number || orderDetails.orderNumber) && (
                  <button 
                    className="btn btn-secondary"
                    onClick={() => {
                      const orderNum = orderDetails.order_number || orderDetails.orderNumber;
                      navigator.clipboard.writeText(orderNum);
                      alert('Order number copied to clipboard!');
                    }}
                  >
                    Copy Order Number
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const isSubmitting = apiLoading;

  return (
    <div className="checkout-page">
      {(submitError || apiError) && (
        <section className="error-section">
          <div className="container">
            <div className="alert alert-error" style={{
              background: '#fee',
              color: '#c33',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              border: '1px solid #fcc'
            }}>
              <p><strong>Error:</strong> {submitError || apiError}</p>
            </div>
          </div>
        </section>
      )}

      <section className="checkout-header">
        <div className="container">
          <h1 className="page-title">Checkout</h1>
          <div className="checkout-steps">
            <div className="step active">
              <span className="step-number">1</span>
              <span className="step-label">Order Details</span>
            </div>
            <div className="step">
              <span className="step-number">2</span>
              <span className="step-label">Payment (COD)</span>
            </div>
            <div className="step">
              <span className="step-number">3</span>
              <span className="step-label">Confirmation</span>
            </div>
          </div>
        </div>
      </section>

      <section className="checkout-content section">
        <div className="container">
          <form onSubmit={handleSubmit} className="checkout-form">
            <div className="checkout-layout">
              <div className="checkout-forms">
                {/* Customer Information */}
                <div className="form-section">
                  <h2>Customer Information</h2>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="fullName">Full Name *</label>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className={formErrors.fullName ? 'error' : ''}
                        placeholder="Enter your full name"
                      />
                      {formErrors.fullName && (
                        <span className="error-message">{formErrors.fullName}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={formErrors.email ? 'error' : ''}
                        placeholder="your@email.com"
                      />
                      {formErrors.email && (
                        <span className="error-message">{formErrors.email}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="phone">Phone Number *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={formErrors.phone ? 'error' : ''}
                        placeholder="+92 300 1234567"
                      />
                      {formErrors.phone && (
                        <span className="error-message">{formErrors.phone}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="company">Company (Optional)</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Your company name"
                      />
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="form-section">
                  <h2>Delivery Address</h2>
                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label htmlFor="address">Street Address *</label>
                      <input
                        type="text"
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className={formErrors.address ? 'error' : ''}
                        placeholder="Enter your street address"
                      />
                      {formErrors.address && (
                        <span className="error-message">{formErrors.address}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="city">City *</label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className={formErrors.city ? 'error' : ''}
                        placeholder="Enter your city"
                      />
                      {formErrors.city && (
                        <span className="error-message">{formErrors.city}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="state">State/Province *</label>
                      <input
                        type="text"
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        className={formErrors.state ? 'error' : ''}
                        placeholder="Enter your state/province"
                      />
                      {formErrors.state && (
                        <span className="error-message">{formErrors.state}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="zipCode">Zip/Postal Code *</label>
                      <input
                        type="text"
                        id="zipCode"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        className={formErrors.zipCode ? 'error' : ''}
                        placeholder="Enter zip/postal code"
                      />
                      {formErrors.zipCode && (
                        <span className="error-message">{formErrors.zipCode}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="country">Country</label>
                      <select
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                      >
                        <option value="Pakistan">Pakistan</option>
                        <option value="India">India</option>
                        <option value="Bangladesh">Bangladesh</option>
                        <option value="UAE">United Arab Emirates</option>
                        <option value="Saudi Arabia">Saudi Arabia</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* ✅ NEW SECTION - Installation Service */}
                <div className="form-section installation-section">
                  <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <svg viewBox="0 0 24 24" width="22" height="22" stroke="#0d9488" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                    </svg>
                    Installation Service
                  </h2>
                  <div className="installation-option">
                    <label className="checkbox-container">
                      <input
                        type="checkbox"
                        checked={requiresInstallation}
                        onChange={(e) => setRequiresInstallation(e.target.checked)}
                      />
                      <span className="checkbox-label-text">
                        <strong>I need professional installation service</strong>
                        <small>Our expert technicians will install and configure your security system</small>
                      </span>
                    </label>
                  </div>

                  {requiresInstallation && (
                    <div className="installation-details">
                      <div className="installation-info-box">
                        <p style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <svg viewBox="0 0 24 24" width="18" height="18" stroke="#0d9488" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                            <polyline points="10 9 9 9 8 9" />
                          </svg>
                          <strong>What's Included:</strong>
                        </p>
                        <ul>
                          <li>Professional installation by certified technicians</li>
                          <li>Complete system configuration and testing</li>
                          <li>Basic training on system operation</li>
                          <li>90-day installation warranty</li>
                        </ul>
                      </div>

                      <div className="form-group full-width">
                        <label>Installation Address</label>
                        <div className="radio-group">
                          <label className="radio-option">
                            <input
                              type="radio"
                              value="same"
                              checked={installationAddress === 'same'}
                              onChange={(e) => setInstallationAddress(e.target.value)}
                            />
                            <span>Same as delivery address</span>
                          </label>
                          <label className="radio-option">
                            <input
                              type="radio"
                              value="different"
                              checked={installationAddress === 'different'}
                              onChange={(e) => setInstallationAddress(e.target.value)}
                            />
                            <span>Different address</span>
                          </label>
                        </div>
                      </div>

                      {installationAddress === 'different' && (
                        <div className="form-group full-width">
                          <label htmlFor="installationAddress">Installation Address *</label>
                          <textarea
                            id="installationAddress"
                            name="installationAddress"
                            value={installationAddressDetails}
                            onChange={(e) => setInstallationAddressDetails(e.target.value)}
                            className={formErrors.installationAddress ? 'error' : ''}
                            placeholder="Enter complete installation address"
                            rows="3"
                          />
                          {formErrors.installationAddress && (
                            <span className="error-message">{formErrors.installationAddress}</span>
                          )}
                        </div>
                      )}

                      <div className="installation-note">
                        <p style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <svg viewBox="0 0 24 24" width="16" height="16" stroke="#92400e" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '2px' }}>
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                          </svg>
                          <span><strong>Next Steps:</strong> Our installation team will contact you within 24 hours to schedule a convenient time for installation.</span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Delivery Preferences */}
                <div className="form-section">
                  <h2>Delivery Preferences</h2>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="deliveryTimePreference">Preferred Delivery Time</label>
                      <select
                        id="deliveryTimePreference"
                        name="deliveryTimePreference"
                        value={formData.deliveryTimePreference}
                        onChange={handleInputChange}
                      >
                        <option value="anytime">Anytime</option>
                        <option value="morning">Morning (9 AM - 12 PM)</option>
                        <option value="afternoon">Afternoon (12 PM - 5 PM)</option>
                        <option value="evening">Evening (5 PM - 8 PM)</option>
                      </select>
                    </div>

                    <div className="form-group full-width">
                      <label htmlFor="specialInstructions">Special Delivery Instructions</label>
                      <textarea
                        id="specialInstructions"
                        name="specialInstructions"
                        value={formData.specialInstructions}
                        onChange={handleInputChange}
                        placeholder="Any special instructions for delivery (e.g., gate code, landmark, etc.)"
                        rows="3"
                      />
                    </div>
                  </div>
                </div>

                {/* Order Notes */}
                <div className="form-section">
                  <h2>Order Notes</h2>
                  <div className="form-group">
                    <label htmlFor="orderNotes">Additional Notes (Optional)</label>
                    <textarea
                      id="orderNotes"
                      name="orderNotes"
                      value={formData.orderNotes}
                      onChange={handleInputChange}
                      placeholder="Any additional notes about your order"
                      rows="4"
                    />
                  </div>
                </div>
              </div>

              {/* Right Column - Order Summary */}
              <div className="order-summary">
                <div className="summary-card">
                  <h2>Order Summary</h2>
                  
                  <div className="order-items">
                    {cart.items.map((item, index) => (
                      <div key={`${item.type}-${item.id}-${index}`} className="order-item">
                        <div className="item-image">
                          {item.image ? (
                            <img src={item.image} alt={item.title} />
                          ) : (
                            <div className="no-image" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {item.type === 'product' ? (
                                <svg viewBox="0 0 24 24" width="20" height="20" stroke="#64748b" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="21 16 12 21 3 16 12 11 21 16" />
                                  <polyline points="12 11 12 21" />
                                  <line x1="12" y1="3" x2="12" y2="11" />
                                  <line x1="12" y1="3" x2="21" y2="8" />
                                  <line x1="12" y1="3" x2="3" y2="8" />
                                  <line x1="3" y1="8" x2="12" y2="11" />
                                  <line x1="21" y1="8" x2="12" y2="11" />
                                </svg>
                              ) : (
                                <svg viewBox="0 0 24 24" width="20" height="20" stroke="#64748b" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="12" cy="12" r="3" />
                                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                                </svg>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="item-details">
                          <h4>{item.title}</h4>
                          <p>Qty: {item.quantity}</p>
                          <p className="item-price">{formatPrice(item.price * item.quantity)}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="pricing-breakdown">
                    <div className="price-line total">
                      <span><strong>Total ({summary.itemCount} items)</strong></span>
                      <span><strong>{formatPrice(summary.totalAmount)}</strong></span>
                    </div>
                  </div>

                  {/* ✅ UPDATED - Show installation info if selected */}
                  {requiresInstallation && (
                    <div className="installation-summary">
                      <div className="install-badge-summary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                        </svg>
                        <span>Installation Service Selected</span>
                      </div>
                      <p className="install-note-summary">Installation will be scheduled after order confirmation</p>
                    </div>
                  )}

                  <div className="payment-method-info">
                    <h3>Payment Method</h3>
                    <div className="cod-info">
                      <div className="cod-icon" style={{ color: '#0d9488', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg viewBox="0 0 24 24" width="30" height="30" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="5" width="20" height="14" rx="2" ry="2" />
                          <line x1="2" y1="10" x2="22" y2="10" />
                          <circle cx="12" cy="14.5" r="1.5" />
                        </svg>
                      </div>
                      <div className="cod-details">
                        <h4>Cash on Delivery (COD)</h4>
                        <p>Pay with cash when your order arrives</p>
                        <ul>
                          <li>No advance payment required</li>
                          <li>Inspect items before payment</li>
                          <li>Exact change appreciated</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="delivery-estimate">
                    <h3>Delivery Information</h3>
                    <p className="delivery-note">
                      We'll call you within 24 hours to confirm your order and delivery details.
                    </p>
                  </div>

                  <div className="checkout-actions">
                    <button 
                      type="submit"
                      className="btn btn-primary btn-large place-order-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <LoadingSpinner type="inline" />
                          <span>Placing Order...</span>
                        </>
                      ) : (
                        'Place Order'
                      )}
                    </button>
                    
                    <button 
                      type="button"
                      className="btn btn-secondary back-to-cart-btn"
                      onClick={() => setCurrentPage('cart')}
                      disabled={isSubmitting}
                    >
                      Back to Cart
                    </button>
                  </div>

                  <div className="security-info">
                    <div className="security-item">
                      <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                      <span>Secure Order Processing</span>
                    </div>
                    <div className="security-item">
                      <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                      <span>Order Confirmation Call</span>
                    </div>
                    <div className="security-item">
                      <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <rect x="1" y="3" width="15" height="13" />
                        <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                        <circle cx="5.5" cy="18.5" r="2.5" />
                        <circle cx="18.5" cy="18.5" r="2.5" />
                      </svg>
                      <span>Fast Delivery</span>
                    </div>
                    <div className="security-item">
                      <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <rect x="2" y="5" width="20" height="14" rx="2" ry="2" />
                        <line x1="2" y1="10" x2="22" y2="10" />
                        <circle cx="12" cy="14.5" r="1.5" />
                      </svg>
                      <span>Pay on Delivery</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Checkout;